import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { ChannelDTO, MessageTargetRef } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { channelApi, messageApi } from '@/api/endpoints';
import { useServers } from '@/stores/servers';
import { useConversations, useNotifications, useReadStates } from '@/stores/social';
import { toast } from '@/stores/ui';

/** Applies a read marker locally (also used when another tab reports one). */
export function applyChannelRead(channelId: string, at: string) {
  const mentions = useReadStates.getState().channels[channelId]?.mentionCount ?? 0;
  useReadStates.getState().markRead(channelId, at);
  if (mentions > 0) {
    useNotifications.setState((s) => ({ unreadCount: Math.max(0, s.unreadCount - mentions) }));
    useNotifications.getState().markRead('all', channelId);
  }
}

const inFlight = new Map<string, ReturnType<typeof setTimeout>>();

/** Marks a channel or DM as read, locally right away and on the server (debounced). */
export function acknowledge(target: MessageTargetRef) {
  const now = new Date().toISOString();
  if (target.channelId) applyChannelRead(target.channelId, now);
  if (target.conversationId) useConversations.getState().markRead(target.conversationId, now);

  const key = target.channelId ?? target.conversationId ?? '';
  const pending = inFlight.get(key);
  if (pending) clearTimeout(pending);
  inFlight.set(
    key,
    setTimeout(() => {
      inFlight.delete(key);
      messageApi.ack(target).catch(() => {
        /* Non-critical: the next ack or bootstrap will reconcile. */
      });
    }, 400),
  );
}

/** Mutes or unmutes a channel for you (no unread dot; mentions still count), here at once and then on the server. */
export async function setChannelMuted(channelId: string, muted: boolean) {
  useReadStates.getState().setMuted(channelId, muted);
  try {
    await channelApi.mute(channelId, muted);
  } catch (err) {
    useReadStates.getState().setMuted(channelId, !muted);
    toast.error(errorMessage(err, muted ? 'Could not mute the channel.' : 'Could not unmute the channel.'));
  }
}

export function isChannelUnread(channel: ChannelDTO, lastReadAt: string | undefined, joinedAt: string): boolean {
  if (channel.type !== 'TEXT' || !channel.lastMessageAt) return false;
  return channel.lastMessageAt > (lastReadAt ?? joinedAt);
}

/** Unread + mention totals for a server, honouring its notification level. */
export function useServerUnread(serverId: string): { unread: boolean; mentions: number } {
  const server = useServers((s) => s.servers[serverId]);
  const channels = useServers(useShallow((s) => Object.values(s.channels).filter((c) => c.serverId === serverId)));
  const reads = useReadStates((s) => s.channels);
  return useMemo(() => {
    if (!server) return { unread: false, mentions: 0 };
    let unread = false;
    let mentions = 0;
    for (const channel of channels) {
      const read = reads[channel.id];
      mentions += read?.mentionCount ?? 0;
      if (!unread && !read?.muted && isChannelUnread(channel, read?.lastReadAt, server.me.joinedAt)) unread = true;
    }
    return { unread: server.me.notificationLevel === 'NOTHING' ? false : unread, mentions };
  }, [server, channels, reads]);
}

/** Total unread DM messages, for the home button badge. */
export function useDmUnreadTotal(): number {
  return useConversations((s) => Object.values(s.conversations).reduce((sum, c) => sum + c.unreadCount, 0));
}
